import { find, orderBy } from 'lodash';
import GhostDialog from 'components/dialog/GhostDialog';
import { Formik as CustomFormik, FormField } from 'components';
import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  PaperProps,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { Select } from '@mui/material';
import { useAddFileCommentMutation, useGetFileExceptionListQuery } from 'api/imageViewer';
import LoadingButton from 'components/@extended/LoadingButton';
import { openDefaultSuccessSnackbar } from 'store/reducers/snackbar';
import ImageViewerComment from './ImageViewerComment';
import ScrollX from 'components/ScrollX';
import CommentIcon from '@mui/icons-material/Comment';
import { setFormClean } from 'store/reducers/formslice';
import Yup from 'utils/yup';
import { ArrowRightAltSharp, KeyboardArrowUpSharp } from '@mui/icons-material';
import { FormikProps } from 'formik';
import React, { useMemo, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { Form, useNavigate } from 'react-router-dom';
import type {
  ImageViewerGetFileLevelResponse,
  GetFileCommentsResponse,
  ImageViwerNavigateFile,
  GetFileExceptionReasonList
} from 'types/image-viewer';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import PdfViewIcon from 'assets/icons/svg/pdf-view.svg?react';
import ExportDownload from 'assets/icons/svg/Export-download.svg?react';
import ExceptionQuestionIcon from 'assets/icons/svg/exception-question.svg?react';
import useScreenSize from 'hooks/useScreenSize';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import InfoIcon from 'assets/icons/svg/info-icon.svg?react';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
// import { ArrowDropDownIcon } from '@mui/x-date-pickers';
import { useGetassuranceFileReportMutation } from 'api/assurance';
import { useFileDownload } from 'hooks/useFileDownload';

const translate = (key: string) => (window as any).translate(key);
type SortOrder = 'asc' | 'desc';

interface TitlebarProps {
  currentFileId: number;
  fileLevelDetails?: ImageViewerGetFileLevelResponse;
  onViewPdfClick?: () => void;
  fileComments?: GetFileCommentsResponse[];
  handleFileNavigation: ImageViwerNavigateFile;
  isSecondaryScreen?: boolean;
  shouldRestrictModification: boolean;
  openLeftPane?: () => void;
  openBottomPane?: () => void;
  showBottomPane?: boolean;
  setFileNavigation?: any;
  setExportLoader?: any;
}

const validationMoveException = Yup.object().shape({
  fileExceptionID: Yup.number().required('Please select an exception reason')
  // comments: Yup.string().max(200, 'Comments must not exceed 200 characters').required('Comments are required')
});

interface MoveToExceptionFormType {
  fileExceptionID: any;
  comments: string;
}

export const Titlebar = ({
  currentFileId,
  fileLevelDetails,
  onViewPdfClick = () => undefined,
  fileComments,
  handleFileNavigation,
  isSecondaryScreen,
  shouldRestrictModification,
  openLeftPane = undefined,
  openBottomPane = undefined,
  showBottomPane = false,
  setFileNavigation,
  setExportLoader
}: TitlebarProps) => {
  const { isDesktop } = useScreenSize();
  const dispatch = useDispatch();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const formRef = useRef<FormikProps<MoveToExceptionFormType> | null>(null);
  const [openFileExceptionForm, setOpenFileExceptionForm] = useState<boolean>(false);
  const [openComments, setOpenComments] = useState<boolean>(false);
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [getassuranceFileReport, { isLoading }] = useGetassuranceFileReportMutation();
  const { handleBlobDownload } = useFileDownload();
  const { data: fileExceptionReasons } = useGetFileExceptionListQuery(undefined, { skip: !openFileExceptionForm });
  const [addCommentAndMoveToException, { isLoading: isLoadingAddComment }] = useAddFileCommentMutation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);
  const { pathname } = window.location;
  const [, , , batchid, fileid] = pathname.split('/');

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDownload = async (type: 'CSV' | 'EXCEL' | 'XML' | 'JSON') => {
    try {
      setExportLoader(true);
      handleClose();
      const { blob } = await getassuranceFileReport({
        fileid,
        batchid,
        exportType: type
      }).unwrap();
      const now = new Date();

      const timestamp =
        String(now.getDate()).padStart(2, '0') +
        String(now.getMonth() + 1).padStart(2, '0') +
        now.getFullYear() +
        '_' +
        String(now.getHours()).padStart(2, '0') +
        String(now.getMinutes()).padStart(2, '0') +
        String(now.getSeconds()).padStart(2, '0') +
        '_' +
        String(now.getMilliseconds()).padStart(3, '0');

      // Determine the file extension based on the reportType (e.g., 'csv', 'xlsx','json','xml')
      let fileExtension = 'csv'; // Default to csv
      if (type === 'CSV') {
        fileExtension = 'csv';
      } else if (type?.toLowerCase() === 'excel') {
        fileExtension = 'xlsx';
      } else if (type?.toLowerCase() === 'json') {
        fileExtension = 'json';
      } else if (type?.toLowerCase() === 'xml') {
        fileExtension = 'xml';
      }

      // Construct the filename dynamically
      const fileName = `${batchid}_${fileid}_${timestamp}.${fileExtension}`;

      // Download the file using the generated blob and filename
      handleBlobDownload(blob, fileName);
      console.log(`Download ${type}`);
      // call your download logic here
    } catch (error) {
      setExportLoader(false);
      console.error('Error during export:', error);
    } finally {
      setExportLoader(false);
    }
  };
  const PaperComponent = React.forwardRef((props: PaperProps, ref: React.Ref<HTMLDivElement>) => (
    <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper ref={ref} {...props} />
    </Draggable>
  ));
  const getShortFileName = (fileName: any) => {
    fileName = String(fileName);
    const maxLength = 20; // Adjust the length as needed
    return fileName.length > maxLength ? `${fileName.substring(0, maxLength)}...` : fileName;
  };

  const onFileExceptionSubmit = async (formData: MoveToExceptionFormType) => {
    try {
      await addCommentAndMoveToException({
        fileID: currentFileId,
        fileExceptionID: formData.fileExceptionID,
        comments: formData.comments
      });
      dispatch(
        openDefaultSuccessSnackbar({
          open: true,
          message: `'${fileLevelDetails?.fileName}' file moved to exception queue successfully`,
          variant: 'alert',
          alert: { color: 'success' },
          close: true
        })
      );
      onCancelFileExceptionForm();
      dispatch(setFormClean());
      setTimeout(() => handleFileNavigation('NEXT')(), 0); // To execute micro task first, So JS engine will call this function after executing the dispatch function
    } catch (e) {
      console.error(e);
    }
  };

  const onCancelFileExceptionForm = () => {
    setOpenFileExceptionForm(false);
    if (formRef.current) {
      formRef.current.resetForm();
    }
  };

  const CustomSortIcon = () => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', fontSize: 'small' }}>
        <Tooltip title={`${translate('pages.exception.labels.sortComments')}`} placement="top">
          <KeyboardArrowUpIcon
            sx={{
              fontSize: '1.1rem',
              color: sortOrder === 'asc' ? 'primary.main' : 'black',
              marginBottom: '-2px'
            }}
          />
        </Tooltip>
        <Tooltip title={`${translate('pages.exception.labels.sortComments')}`} placement="top">
          <KeyboardArrowDownIcon
            sx={{
              fontSize: '1.1rem',
              color: sortOrder === 'desc' ? 'primary.main' : 'black',
              marginTop: '-2px'
            }}
          />
        </Tooltip>
      </Box>
    );
  };

  const toggleSortOrder = () => {
    setSortOrder((prevOrder: any) => (prevOrder === 'asc' ? 'desc' : 'asc'));
  };

  const preventOverLimit = (e: any) => {
    // Check if the key will add a character and if we're at the limit
    if (
      e.target.value.length >= 200 &&
      e.key.length === 1 && // Only printable characters
      !e.ctrlKey &&
      !e.metaKey && // Allow Ctrl+A, Ctrl+V, etc.
      e.key !== 'Backspace' &&
      e.key !== 'Delete'
    ) {
      e.preventDefault();
    }
  };
  const allowOnlyAllowedCharacters = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedRegex = /^[a-zA-Z0-9 .,']+$/;

    if (
      e.ctrlKey ||
      e.metaKey ||
      ['Backspace', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Delete'].includes(e.key)
    ) {
      return;
    }

    if (!allowedRegex.test(e.key)) {
      e.preventDefault();
    }
  };

  // Use: onKeyDown={preventOverLimit}

  const sortedFileComments = useMemo(() => orderBy(fileComments, ['createOn'], [sortOrder]), [fileComments, sortOrder]);

  return (
    <Box className={`configuration-title-bar ${!isDesktop && 'configuration-title-bar-mobile'}`} sx={{ position: 'relative' }}>
      <Box className={`title-field-wrapper ${!isDesktop && 'title-field-wrapper-mobile'}`}>
        <Box>
          <Typography className="title-field-label">FILE NAME</Typography>
          <Tooltip title={fileLevelDetails?.fileName || 'Unknown'} arrow>
            <Box>
              <Typography className="title-field-value" noWrap>
                {getShortFileName(fileLevelDetails?.fileName || 'Unknown')}
              </Typography>
            </Box>
          </Tooltip>
        </Box>

        <Box>
          <Typography className="title-field-label">Batch ID</Typography>
          <Tooltip title={fileLevelDetails?.batchID ? String(fileLevelDetails?.batchID) : 'Unknown'} arrow>
            <Box>
              <Typography className="title-field-value" noWrap>
                {getShortFileName(fileLevelDetails?.batchID || 'Unknown')}
              </Typography>{' '}
            </Box>
          </Tooltip>
        </Box>

        {!(location.state?.isFromDPC || shouldRestrictModification) && (
          <Box>
            <Typography className="title-field-label">EXTRACTED</Typography>
            <Typography className="title-field-value" noWrap>
              {fileLevelDetails?.extractedCount || 0}
            </Typography>
          </Box>
        )}

        {!(location.state?.isFromDPC || shouldRestrictModification) && (
          <Box>
            <Typography className="title-field-label">MODIFIED</Typography>
            <Typography className="title-field-value" noWrap>
              {fileLevelDetails?.modifiedCount || 0}
            </Typography>
          </Box>
        )}

        {!(location.state?.isFromDPC || shouldRestrictModification) && (
          <Box>
            <Typography className="title-field-label">ACCURACY</Typography>
            <Typography className="title-field-value" noWrap>
              {Number(fileLevelDetails?.accuracy || 0).toFixed(2)}%
            </Typography>
          </Box>
        )}
      </Box>

      {!isSecondaryScreen && (
        <>
          <Stack
            direction="row"
            gap="8px"
            alignItems="center"
            divider={<Divider orientation="vertical" flexItem className="image-viewer-titlebar-icon-button-divider" />}
            sx={{ position: 'sticky', right: 0 }}
          >
            <Button
              onClick={() => {
                navigate(-1);
              }}
              size={!!openLeftPane ? 'small' : 'medium'}
              variant="outlined"
              className="globalBackButton"
              aria-label="Back"
              startIcon={<ArrowBackIosIcon />}
              sx={{ backgroundColor: theme.palette.background.default }}
            >
              {translate('elements.buttons.back')}
            </Button>
            {!!openLeftPane && (
              <Tooltip placement="top" title={'Navigate to previous file '}>
                <IconButton
                  onClick={openLeftPane}
                  className="image-viewer-titlebar-icon-button"
                  sx={{ backgroundColor: theme.palette.background.default }}
                >
                  <ArrowRightAltSharp color="primary" />
                </IconButton>
              </Tooltip>
            )}
            {fileComments && fileComments.length > 0 && (
              <Tooltip placement="top" title={'View comments from all users of this file'}>
                <IconButton
                  className="image-viewer-titlebar-icon-button"
                  onClick={() => setOpenComments(true)}
                  sx={{ backgroundColor: theme.palette.background.default }}
                >
                  <CommentIcon color="primary" />
                </IconButton>
              </Tooltip>
            )}
            <ButtonGroup className="image-viewer-titlebar-icon-button-group" sx={{ backgroundColor: theme.palette.background.default }}>
              <Tooltip placement="top" title={'Navigate to previous file '}>
                <IconButton
                  onClick={() => {
                    handleFileNavigation('PREV')(true);
                    setFileNavigation(true);
                  }}
                >
                  <KeyboardDoubleArrowLeftIcon color="primary" />
                </IconButton>
              </Tooltip>
              <Tooltip placement="top" title={'Navigate to next file '}>
                <IconButton
                  onClick={() => {
                    handleFileNavigation('NEXT')(true);
                    setFileNavigation(true);
                  }}
                >
                  <KeyboardDoubleArrowRightIcon color="primary" />
                </IconButton>
              </Tooltip>
            </ButtonGroup>
            <Stack gap="8px" direction="row" alignItems="center">
              <Tooltip placement="top" title={'Open current file in PDF format'}>
                <IconButton className="image-viewer-titlebar-icon-button" sx={{ backgroundColor: theme.palette.background.default }}>
                  <PdfViewIcon onClick={onViewPdfClick} />
                </IconButton>
              </Tooltip>
              {shouldRestrictModification && !location?.state?.isFromDPC && location.state?.batchType === 'Verified' && (
                <>
                  <Tooltip placement="top" title={`${translate('pages.reports.processReport.exportSchedularResport')}`}>
                    <IconButton
                      className="image-viewer-titlebar-icon-button"
                      sx={{ backgroundColor: theme.palette.background.default }}
                      onClick={handleOpen}
                    >
                      <ExportDownload />
                      {/* <ArrowDropDownIcon sx={{ fontSize: 16, color: '#000' }} /> */}
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right'
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right'
                    }}
                  >
                    <MenuItem disabled>Downloaded At</MenuItem>

                    <MenuItem onClick={() => handleDownload('CSV')}>CSV</MenuItem>

                    <MenuItem onClick={() => handleDownload('EXCEL')}>EXCEL</MenuItem>

                    <MenuItem onClick={() => handleDownload('XML')}>XML</MenuItem>

                    <MenuItem onClick={() => handleDownload('JSON')}>JSON</MenuItem>
                  </Menu>
                </>
              )}

              {location?.state?.batchTypeID !== 2 && !shouldRestrictModification && !location?.state?.isFromDPC && (
                <Tooltip placement="top" title={'Move this file to exception queue'}>
                  <IconButton
                    onClick={() => setOpenFileExceptionForm(true)}
                    disabled={location?.state?.isDisabled}
                    className="image-viewer-titlebar-icon-button"
                    sx={{ backgroundColor: theme.palette.background.default }}
                  >
                    <ExceptionQuestionIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            {!!openBottomPane && showBottomPane && (
              <Tooltip placement="top" title={'Navigate to previous file '}>
                <IconButton onClick={openBottomPane} className="image-viewer-titlebar-icon-button">
                  <KeyboardArrowUpSharp color="primary" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          <GhostDialog open={openFileExceptionForm} onClose={() => {}} PaperComponent={PaperComponent} customMaxWidth="650px">
            <CustomFormik
              onSubmit={onFileExceptionSubmit}
              initialValues={{
                fileExceptionID: '',
                comments: ''
              }}
              validationSchema={validationMoveException}
              innerRef={formRef}
            >
              {({ errors, touched, handleSubmit }) => (
                <Form>
                  <Box
                    className="move-to-exception-form-container popup-content-background-image"
                    p="30px"
                    sx={{
                      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.background.default
                    }}
                  >
                    <Grid
                      xs={12}
                      container
                      justifyContent="space-between"
                      alignItems="center"
                      style={{ cursor: 'move' }}
                      id="draggable-dialog-title"
                    >
                      <Grid item>
                        <Typography variant="h3" className="popup-title" mb="5px">
                          Move To Exception
                        </Typography>
                      </Grid>
                      <Grid item>
                        <InfoIcon />
                      </Grid>
                    </Grid>
                    <Divider sx={{ my: 1 }} className="popup-tittle-divider" />
                    <Stack gap="20px" mt="30px">
                      <Grid container columnSpacing={1}>
                        <Grid item xs={12} md={4}>
                          <Typography fontSize="14px" mt={1}>
                            Select Exception Reason*
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={8}>
                          <FormField
                            name="fileExceptionID"
                            label=""
                            component={Select}
                            error={
                              touched.fileExceptionID && typeof errors.fileExceptionID === 'string' ? errors.fileExceptionID : undefined
                            }
                            required
                            displayEmpty
                            renderValue={(selected: string | any[]) => {
                              if (!selected || !fileExceptionReasons) {
                                return (
                                  <MenuItem sx={{ padding: 0 }} value="" disabled>
                                    Select
                                  </MenuItem>
                                );
                              }
                              const reasonDetails: GetFileExceptionReasonList | undefined = find(fileExceptionReasons, {
                                fileExceptionID: selected
                              }) as GetFileExceptionReasonList;
                              if (reasonDetails) {
                                return reasonDetails.fileException;
                              }
                            }}
                            reserveSpaceForErrorMessage
                            sx={{ backgroundColor: theme.palette.background.default }}
                          >
                            <MenuItem value={0} disabled>
                              Select
                            </MenuItem>
                            {fileExceptionReasons &&
                              fileExceptionReasons.map((reason) => (
                                <MenuItem key={reason.fileExceptionID} value={reason.fileExceptionID}>
                                  {reason.fileException}
                                </MenuItem>
                              ))}
                          </FormField>
                        </Grid>
                      </Grid>
                      <Grid container columnSpacing={1}>
                        <Grid item xs={12} md={4}>
                          <Typography fontSize="14px" mt={1}>
                            Comment
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={8}>
                          <FormField
                            name="comments"
                            label=""
                            component={TextField}
                            // error={touched.comments && errors.comments}
                            required
                            minRows={1}
                            maxLength={200}
                            placeholder="Write a brief about this process in less than 200 characters."
                            multiline
                            sx={{ backgroundColor: theme.palette.background.default }}
                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                              preventOverLimit(e);
                              allowOnlyAllowedCharacters(e); // <-- Restrict special characters here
                            }}
                            onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
                              const pastedText = e.clipboardData.getData('text');
                              const allowedRegex = /^[a-zA-Z0-9 .,!?'"()\-_\n\r\b\t]+$/;
                              if (!allowedRegex.test(pastedText)) {
                                e.preventDefault();
                              }
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Stack>
                  </Box>
                  <Box p="30px" sx={{ backgroundColor: theme.palette.background.paper }}>
                    <Grid container justifyContent="space-around" alignItems="center" columnSpacing="30px">
                      <Grid item>
                        <Button variant="contained" className="buttonSecondary" onClick={onCancelFileExceptionForm}>
                          CANCEL
                        </Button>
                      </Grid>
                      <Grid item>
                        <LoadingButton
                          variant="contained"
                          color="primary"
                          type="button"
                          onClick={handleSubmit}
                          loading={isLoadingAddComment}
                        >
                          Move
                        </LoadingButton>
                      </Grid>
                    </Grid>
                  </Box>
                </Form>
              )}
            </CustomFormik>
          </GhostDialog>
          {fileComments && fileComments.length > 0 && (
            <GhostDialog open={openComments} onClose={() => {}} customMaxWidth="550px">
              <Box className="move-to-exception-form-container" p="30px" pb="0px">
                <Grid container justifyContent="space-between" alignItems={'flex-end'}>
                  <Grid item>
                    <Typography lineHeight="24px" variant="h3" className="popup-title">
                      Comment
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography textAlign="right" fontSize="10px">
                      {translate('pages.review.verification.labels.fileName')}
                    </Typography>
                    <Typography textAlign="right" fontSize="12px" className="text-bold">
                      {fileLevelDetails?.fileName}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
              <Box className="qc-comments-section">
                <Box className="file-comments-container" sx={{ backgroundColor: theme.palette.background.paper }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" px="10px" mt="10px" mb="2px">
                    <Typography variant="h6" fontWeight="bold">
                      COMMENTS HISTORY
                    </Typography>
                    <Box
                      onClick={() => {
                        toggleSortOrder();
                      }}
                      sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                    >
                      <Typography variant="body2" color="textSecondary" sx={{ mr: 0.5 }}>
                        {translate('pages.exception.labels.SORT')}
                      </Typography>

                      <CustomSortIcon />
                    </Box>
                  </Stack>
                  <ScrollX sx={{ height: '300px' }}>
                    {sortedFileComments.map((comment) => (
                      <ImageViewerComment key={comment.createOn} comment={comment} />
                    ))}
                  </ScrollX>
                  <Box p="10px" mt="20px">
                    <Grid container justifyContent="flex-end" alignItems="center" columnSpacing="30px">
                      <Grid item>
                        <Button variant="contained" className="buttonSecondary" onClick={() => setOpenComments(false)}>
                          {translate('elements.buttons.close')}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Box>
            </GhostDialog>
          )}
        </>
      )}
    </Box>
  );
};
